import React, { useMemo } from 'react'
import { Empty, Flex, Skeleton, Typography } from 'antd'
import { useIntl } from 'react-intl'
import {
  BankOutlined,
  CreditCardOutlined,
  QrcodeOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  WalletOutlined,
} from '@ant-design/icons'

import { PROVIDER_ID } from '@/utils/static'
import { useFinancePaymentConnectSetting } from '@/utils/queries'
import {
  formatKledoChargeReadable,
  groupKledoPaymentMethods,
  METHOD_LOGO_BY_ID,
} from '@/components/apps/Cashier/Payment/kledoPaymentMethodUtils'
import styles from './styles.module.scss'

const { Title, Text, Paragraph } = Typography

const CATEGORY_ICON = {
  virtual_account: BankOutlined,
  e_wallet: WalletOutlined,
  qris: QrcodeOutlined,
  over_the_counter: ShopOutlined,
  card: CreditCardOutlined,
  paylater: CreditCardOutlined,
  others: BankOutlined,
}

const NumberedHeading = ({ index, title }) => (
  <Flex align="center" gap={12} className={styles.headingRow}>
    <span className={styles.numberBadge}>{index}</span>
    <Title level={5} className={styles.headingTitle}>
      {title}
    </Title>
  </Flex>
)

const CategoryCard = ({ section, formatMessage }) => {
  const Icon = CATEGORY_ICON[section.key] || BankOutlined
  return (
    <div className={styles.categoryCard}>
      <Flex align="center" gap={10} className={styles.categoryHeader}>
        <span className={styles.categoryIcon}>
          <Icon />
        </span>
        <Text className={styles.categoryTitle}>{formatMessage({ id: section.titleKey })}</Text>
      </Flex>
      <Flex wrap="wrap" gap={6} className={styles.categoryLogos}>
        {section.methods.map((method) => {
          const logo = METHOD_LOGO_BY_ID[method.id]
          if (logo) {
            return (
              <span key={method.id} className={styles.logoChip} title={method.name}>
                <img
                  src={logo}
                  alt={method.name}
                  className={styles.logoImg}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              </span>
            )
          }
          return (
            <span key={method.id} className={styles.textChip}>
              {method.name}
            </span>
          )
        })}
      </Flex>
    </div>
  )
}

const FeeRow = ({ method }) => {
  const logo = METHOD_LOGO_BY_ID[method.id]
  return (
    <Flex align="center" justify="space-between" gap={12} className={styles.feeRow}>
      <Flex align="center" gap={10} className={styles.feeRowLeft}>
        <span className={styles.feeLogoWrap}>
          {logo ? (
            <img
              src={logo}
              alt={method.name}
              className={styles.feeLogo}
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          ) : (
            <BankOutlined />
          )}
        </span>
        <Text className={styles.feeMethodName}>{method.name}</Text>
      </Flex>
      <Text className={styles.feeValue}>{formatKledoChargeReadable(method.charge)}</Text>
    </Flex>
  )
}

const DisbursementStep = ({ icon: Icon, title, description }) => (
  <Flex gap={12} align="flex-start" className={styles.stepRow}>
    <span className={styles.stepIcon}>
      <Icon />
    </span>
    <div className={styles.stepBody}>
      <Text className={styles.stepTitle}>{title}</Text>
      <Text className={styles.stepDescription}>{description}</Text>
    </div>
  </Flex>
)

const KledoPaymentConnectInfo = ({ availableMethods: methodsProp }) => {
  const { formatMessage } = useIntl()
  const shouldSelfFetch = !methodsProp
  const { data, status } = useFinancePaymentConnectSetting({
    id: PROVIDER_ID.KLEDO,
    options: { enabled: shouldSelfFetch },
  })
  const methods = useMemo(
    () => methodsProp ?? data?.kledo_available_payment_methods ?? [],
    [methodsProp, data?.kledo_available_payment_methods],
  )
  const groupedSections = useMemo(
    () => (methods.length ? groupKledoPaymentMethods(methods) : []),
    [methods],
  )
  const isLoadingFees = shouldSelfFetch && status === 'loading'

  return (
    <Flex vertical gap={28} className={styles.container}>
      {/* Section 1: Metode pembayaran */}
      <section className={styles.section}>
        <NumberedHeading
          index={1}
          title={formatMessage({ id: 'appOnboarding.paymentGateway.info.section1_title' })}
        />
        <Paragraph className={styles.sectionBody}>
          {formatMessage({ id: 'appOnboarding.paymentGateway.info.section1_body' })}
        </Paragraph>
        {groupedSections.length > 0 ? (
          <div className={styles.categoryGrid}>
            {groupedSections.map((section) => (
              <CategoryCard key={section.key} section={section} formatMessage={formatMessage} />
            ))}
          </div>
        ) : (
          !isLoadingFees && (
            <Empty
              image={null}
              description={formatMessage({
                id: 'appOnboarding.paymentGateway.info.fee_unavailable',
              })}
            />
          )
        )}
      </section>

      {/* Section 2: Biaya transaksi */}
      <section className={styles.section}>
        <NumberedHeading
          index={2}
          title={formatMessage({ id: 'appOnboarding.paymentGateway.info.section2_title' })}
        />
        <Paragraph className={styles.sectionBody}>
          {formatMessage({ id: 'appOnboarding.paymentGateway.info.section2_body' })}
        </Paragraph>

        {isLoadingFees ? (
          <Skeleton active paragraph={{ rows: 6 }} title={false} />
        ) : groupedSections.length > 0 ? (
          <div className={styles.feeGroupsList}>
            {groupedSections.map((section) => (
              <div key={section.key} className={styles.feeGroup}>
                <Text className={styles.feeGroupTitle}>
                  {formatMessage({ id: section.titleKey })}
                </Text>
                <div className={styles.feeRows}>
                  {section.methods.map((method) => (
                    <FeeRow key={method.id} method={method} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty
            image={null}
            description={formatMessage({
              id: 'appOnboarding.paymentGateway.info.fee_unavailable',
            })}
          />
        )}

        <div className={styles.chargeToBlock}>
          <Text className={styles.chargeToTitle}>
            {formatMessage({
              id: 'appOnboarding.paymentGateway.info.section2_fee_charged_to_title',
            })}
          </Text>
          <ul className={styles.chargeToList}>
            <li>
              {formatMessage({ id: 'appOnboarding.paymentGateway.info.section2_fee_to_buyer' })}
            </li>
            <li>
              {formatMessage({ id: 'appOnboarding.paymentGateway.info.section2_fee_to_seller' })}
            </li>
          </ul>
          <Text type="secondary" className={styles.chargeToHint}>
            {formatMessage({
              id: 'appOnboarding.paymentGateway.info.section2_fee_settings_hint',
            })}
          </Text>
        </div>
      </section>

      {/* Section 3: Pencairan dana */}
      <section className={styles.section}>
        <NumberedHeading
          index={3}
          title={formatMessage({ id: 'appOnboarding.paymentGateway.info.section3_title' })}
        />
        <Paragraph className={styles.sectionBody}>
          {formatMessage({ id: 'appOnboarding.paymentGateway.info.section3_body' })}
        </Paragraph>
        <Flex vertical gap={14} className={styles.stepList}>
          <DisbursementStep
            icon={BankOutlined}
            title={formatMessage({
              id: 'appOnboarding.paymentGateway.info.section3_step1_title',
            })}
            description={formatMessage({
              id: 'appOnboarding.paymentGateway.info.section3_step1',
            })}
          />
          <DisbursementStep
            icon={SafetyCertificateOutlined}
            title={formatMessage({
              id: 'appOnboarding.paymentGateway.info.section3_step2_title',
            })}
            description={formatMessage({
              id: 'appOnboarding.paymentGateway.info.section3_step2',
            })}
          />
          <DisbursementStep
            icon={WalletOutlined}
            title={formatMessage({
              id: 'appOnboarding.paymentGateway.info.section3_step3_title',
            })}
            description={formatMessage({
              id: 'appOnboarding.paymentGateway.info.section3_step3',
            })}
          />
        </Flex>
        <Text type="secondary" className={styles.footnote}>
          {formatMessage({ id: 'appOnboarding.paymentGateway.info.section3_footnote' })}
        </Text>
      </section>
    </Flex>
  )
}

export default KledoPaymentConnectInfo
